import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  CalendarDays, Clock, User, LogOut, Loader2, Save, KeyRound, Plus, Trash2,
  CalendarOff, CalendarClock, ShieldCheck, CheckCircle2, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DOW = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const defaultHours = () => Object.fromEntries(DOW.map(([d]) => [d, { open: "09:00", close: "17:00", closed: d === "sat" || d === "sun" }]));
const csv = (a) => (a || []).join(", ");
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const STATUS_COLOR = { booked: "bg-blue-100 text-blue-700", confirmed: "bg-emerald-100 text-emerald-700", completed: "bg-slate-200 text-slate-600", cancelled: "bg-red-100 text-red-600" };

export default function StaffPortal() {
  const { user, logout, refresh } = useAuth();
  const [tab, setTab] = useState("schedule");
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const [m, o] = await Promise.all([api.get("/staff-portal/me"), api.get("/staff-portal/overview")]);
      setMe(m.data); setOverview(o.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user && user.role === "staff" && !user.must_change_password) loadMe(); }, [user, loadMe]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "staff") return <Navigate to="/dashboard" replace />;
  if (user.must_change_password) return <ForcePasswordChange onDone={refresh} name={user.name} onLogout={logout} />;

  const perms = me?.permissions || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-portal">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <p className="font-heading font-bold text-slate-900 text-sm" data-testid="staff-portal-name">{user.name}</p>
              <p className="text-[11px] text-slate-400">{me?.business_name || ""}</p>
            </div>
          </div>
          <Button data-testid="staff-logout" variant="ghost" size="sm" onClick={logout} className="gap-1.5 text-slate-500"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-6">
        {/* Overview cards */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[["Today", overview?.today_count], ["Upcoming", overview?.upcoming_count], [perms.view_all_appointments ? "All bookings" : "My bookings", overview?.total]].map(([lbl, v]) => (
            <div key={lbl} className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-2xl font-bold text-slate-900">{v ?? "—"}</p>
              <p className="text-xs text-slate-400">{lbl}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-white border border-slate-200 rounded-xl p-1 w-fit">
          {[["schedule", "Schedule", CalendarDays], ["availability", "Availability", Clock], ["profile", "Profile", User]].map(([k, lbl, Icon]) => (
            <button key={k} data-testid={`staff-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="h-4 w-4" /> {lbl}
            </button>
          ))}
        </div>

        {loading ? <div className="flex items-center gap-2 text-slate-400 py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div> : (
          <>
            {tab === "schedule" && <Schedule />}
            {tab === "availability" && <Availability />}
            {tab === "profile" && <Profile onSaved={loadMe} />}
          </>
        )}
      </div>
    </div>
  );
}

function ForcePasswordChange({ onDone, name, onLogout }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (nw.length < 6) return setErr("New password must be at least 6 characters");
    if (nw !== cf) return setErr("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated — welcome!");
      await onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Could not change password"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="staff-force-password">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto"><KeyRound className="h-6 w-6" /></div>
        <h1 className="font-heading text-xl font-bold text-center mt-4">Set your password</h1>
        <p className="text-slate-500 text-sm text-center mt-1">Hi {name}, please choose a new password to continue.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><Label className="text-xs">Temporary password</Label><Input data-testid="fp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required className="mt-1" /></div>
          <div><Label className="text-xs">New password</Label><Input data-testid="fp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} required className="mt-1" /></div>
          <div><Label className="text-xs">Confirm new password</Label><Input data-testid="fp-confirm" type="password" value={cf} onChange={(e) => setCf(e.target.value)} required className="mt-1" /></div>
          {err && <p data-testid="fp-error" className="text-sm text-red-600">{err}</p>}
          <Button data-testid="fp-submit" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save password</Button>
        </form>
        <button onClick={onLogout} className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600">Sign out</button>
      </div>
    </div>
  );
}

function Schedule() {
  const [data, setData] = useState({ appointments: [], can_edit: false, view_all: false });
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ services: [], staff: [], can_book_others: false, self_id: "", self_name: "" });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", service: "", staff_id: "", date: "", time: "", notes: "" });
  const load = useCallback(async () => {
    try { const r = await api.get("/staff-portal/appointments"); setData(r.data); } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); api.get("/staff-portal/booking-options").then((r) => setOpts(r.data)).catch(() => {}); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStatus = async (id, status) => {
    try { await api.patch(`/staff-portal/appointments/${id}`, { status }); toast.success(`Marked ${status}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const book = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.date || !form.time) return toast.error("Please choose a date and time");
    setSaving(true);
    try {
      await api.post("/staff-portal/appointments", {
        customer_name: form.customer_name, phone: form.phone, service: form.service,
        date: form.date, time: form.time, staff_id: form.staff_id === "self" ? "" : form.staff_id, notes: form.notes,
      });
      toast.success("Booking added");
      setForm({ customer_name: "", phone: "", service: "", staff_id: "", date: "", time: "", notes: "" });
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add booking"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  const appts = data.appointments || [];
  return (
    <div data-testid="staff-schedule">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">{data.view_all ? "Showing all business appointments." : "Showing your appointments."} {appts.length} total.</p>
        <Button data-testid="staff-add-booking" onClick={() => setOpen(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> Add booking</Button>
      </div>
      {appts.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400 text-sm">No appointments yet.</div>}
      <div className="space-y-2">
        {appts.map((a) => (
          <div key={a.id} data-testid={`staff-appt-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4">
            <div className="text-center shrink-0 w-16">
              <p className="text-xs text-slate-400">{(a.start || "").slice(5, 10)}</p>
              <p className="font-bold text-slate-900">{(a.start || "").slice(11, 16) || "—"}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate">{a.service || a.title || "Appointment"}</p>
              <p className="text-sm text-slate-500 truncate">{a.customer_name || a.customer || "—"}{a.staff_name ? ` · ${a.staff_name}` : ""}</p>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STATUS_COLOR[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status || "booked"}</span>
            {data.can_edit && a.status !== "cancelled" && a.status !== "completed" && (
              <div className="flex gap-1 shrink-0">
                <Button data-testid={`appt-complete-${a.id}`} variant="ghost" size="icon" title="Mark completed" onClick={() => setStatus(a.id, "completed")} className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></Button>
                <Button data-testid={`appt-cancel-${a.id}`} variant="ghost" size="icon" title="Cancel" onClick={() => setStatus(a.id, "cancelled")} className="h-8 w-8 text-red-500 hover:bg-red-50"><XCircle className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-booking-dialog">
          <DialogHeader><DialogTitle className="font-heading">New booking</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Customer name *</Label><Input data-testid="sb-customer" value={form.customer_name} onChange={(e) => setF("customer_name", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Service</Label>
              {opts.services.length > 0 ? (
                <Select value={form.service} onValueChange={(v) => setF("service", v)}>
                  <SelectTrigger data-testid="sb-service" className="mt-1"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>{opts.services.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}{s.duration ? ` · ${s.duration} min` : ""}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input data-testid="sb-service" value={form.service} onChange={(e) => setF("service", e.target.value)} className="mt-1" placeholder="Service" />}
            </div>
            {opts.can_book_others && opts.staff.length > 0 && (
              <div>
                <Label className="text-xs">Staff member</Label>
                <Select value={form.staff_id || "self"} onValueChange={(v) => setF("staff_id", v)}>
                  <SelectTrigger data-testid="sb-staff" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="self">Me ({opts.self_name})</SelectItem>{opts.staff.filter((s) => s.id !== opts.self_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date *</Label><Input data-testid="sb-date" type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Time *</Label><Input data-testid="sb-time" type="time" value={form.time} onChange={(e) => setF("time", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="mt-1" /></div>
            <p className="text-xs text-slate-400">{opts.can_book_others ? "Availability is checked automatically to prevent double-booking." : "This will be booked to you. Availability is checked automatically."}</p>
          </div>
          <DialogFooter>
            <Button data-testid="sb-save" onClick={book} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? "Booking…" : "Book appointment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Availability() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/staff-portal/availability").then((r) => setD({ ...r.data, working_hours: r.data.working_hours && Object.keys(r.data.working_hours).length ? r.data.working_hours : defaultHours() })).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  const canEdit = d?.can_edit;
  const setHour = (day, k, v) => setD((s) => ({ ...s, working_hours: { ...s.working_hours, [day]: { ...s.working_hours[day], [k]: v } } }));
  const addBreak = () => setD((s) => ({ ...s, break_times: [...(s.break_times || []), { day: "", start: "12:00", end: "13:00" }] }));
  const addLeave = () => setD((s) => ({ ...s, leave_dates: [...(s.leave_dates || []), { start: "", end: "", reason: "" }] }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/staff-portal/availability", { working_hours: d.working_hours, break_times: d.break_times, leave_dates: d.leave_dates, buffer_minutes: Number(d.buffer_minutes) || 0 }); toast.success("Availability saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <div data-testid="staff-availability" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
      {!canEdit && <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">Your manager has set your availability to view-only.</div>}
      <div>
        <Label className="text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Working hours</Label>
        <div className="mt-2 space-y-1.5">
          {DOW.map(([day, lbl]) => { const h = d.working_hours[day] || { open: "09:00", close: "17:00", closed: true }; return (
            <div key={day} className="flex items-center gap-2 text-sm">
              <span className="w-10 text-slate-500">{lbl}</span>
              <Switch data-testid={`avail-${day}`} disabled={!canEdit} checked={!h.closed} onCheckedChange={(v) => setHour(day, "closed", !v)} />
              {h.closed ? <span className="text-slate-400 text-xs">Closed</span> : (<>
                <Input type="time" disabled={!canEdit} value={h.open} onChange={(e) => setHour(day, "open", e.target.value)} className="h-8 w-28" />
                <span className="text-slate-400">–</span>
                <Input type="time" disabled={!canEdit} value={h.close} onChange={(e) => setHour(day, "close", e.target.value)} className="h-8 w-28" />
              </>)}
            </div>); })}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between"><Label className="text-xs flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Break times</Label>
          {canEdit && <Button variant="ghost" size="sm" onClick={addBreak} className="text-blue-600 gap-1 h-7"><Plus className="h-3.5 w-3.5" /> Add break</Button>}</div>
        {(d.break_times || []).map((b, i) => (
          <div key={i} className="flex items-center gap-2 mt-1.5">
            <Select value={b.day || "all"} onValueChange={(v) => setD((s) => ({ ...s, break_times: s.break_times.map((x, j) => j === i ? { ...x, day: v === "all" ? "" : v } : x) }))}>
              <SelectTrigger className="h-8 w-28" disabled={!canEdit}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Every day</SelectItem>{DOW.map(([dd, l]) => <SelectItem key={dd} value={dd}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="time" disabled={!canEdit} value={b.start} onChange={(e) => setD((s) => ({ ...s, break_times: s.break_times.map((x, j) => j === i ? { ...x, start: e.target.value } : x) }))} className="h-8 w-28" />
            <span className="text-slate-400">–</span>
            <Input type="time" disabled={!canEdit} value={b.end} onChange={(e) => setD((s) => ({ ...s, break_times: s.break_times.map((x, j) => j === i ? { ...x, end: e.target.value } : x) }))} className="h-8 w-28" />
            {canEdit && <Button variant="ghost" size="icon" onClick={() => setD((s) => ({ ...s, break_times: s.break_times.filter((_, j) => j !== i) }))} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between"><Label className="text-xs flex items-center gap-1"><CalendarOff className="h-3.5 w-3.5" /> Holiday / leave</Label>
          {canEdit && <Button variant="ghost" size="sm" onClick={addLeave} className="text-blue-600 gap-1 h-7"><Plus className="h-3.5 w-3.5" /> Add leave</Button>}</div>
        {(d.leave_dates || []).map((l, i) => (
          <div key={i} className="flex items-center gap-2 mt-1.5">
            <Input type="date" disabled={!canEdit} value={l.start} onChange={(e) => setD((s) => ({ ...s, leave_dates: s.leave_dates.map((x, j) => j === i ? { ...x, start: e.target.value } : x) }))} className="h-8" />
            <span className="text-slate-400">→</span>
            <Input type="date" disabled={!canEdit} value={l.end} onChange={(e) => setD((s) => ({ ...s, leave_dates: s.leave_dates.map((x, j) => j === i ? { ...x, end: e.target.value } : x) }))} className="h-8" />
            <Input disabled={!canEdit} value={l.reason} onChange={(e) => setD((s) => ({ ...s, leave_dates: s.leave_dates.map((x, j) => j === i ? { ...x, reason: e.target.value } : x) }))} placeholder="Reason" className="h-8" />
            {canEdit && <Button variant="ghost" size="icon" onClick={() => setD((s) => ({ ...s, leave_dates: s.leave_dates.filter((_, j) => j !== i) }))} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
      {canEdit && <Button data-testid="avail-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save availability</Button>}
    </div>
  );
}

function Profile({ onSaved }) {
  const [p, setP] = useState(null); const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/staff-portal/profile").then((r) => { setP(r.data.profile); setCanEdit(r.data.can_edit); }).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/staff-portal/profile", { title: p.title, bio: p.bio, phone: p.phone, languages: p.languages, specialisations: p.specialisations, years_experience: p.years_experience === "" ? null : Number(p.years_experience) });
      toast.success("Profile saved"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <div data-testid="staff-profile" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 max-w-2xl">
      {!canEdit && <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">Your profile is view-only. Ask your manager to enable editing.</div>}
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl">{(p.name || "?").slice(0, 1).toUpperCase()}</div>
        <div><p className="font-semibold text-slate-900">{p.name}</p><p className="text-sm text-slate-400">{p.login_email || p.email}</p></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-500">Job title</Label><Input data-testid="profile-title" disabled={!canEdit} value={p.title || ""} onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs text-slate-500">Phone</Label><Input disabled={!canEdit} value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs text-slate-500">Languages (comma separated)</Label><Input disabled={!canEdit} value={csv(p.languages)} onChange={(e) => set("languages", toArr(e.target.value))} className="mt-1" /></div>
        <div><Label className="text-xs text-slate-500">Specialisations (comma separated)</Label><Input data-testid="profile-spec" disabled={!canEdit} value={csv(p.specialisations)} onChange={(e) => set("specialisations", toArr(e.target.value))} className="mt-1" /></div>
        <div><Label className="text-xs text-slate-500">Years of experience</Label><Input type="number" disabled={!canEdit} value={p.years_experience ?? ""} onChange={(e) => set("years_experience", e.target.value)} className="mt-1" /></div>
      </div>
      <div><Label className="text-xs text-slate-500">Biography</Label><Textarea disabled={!canEdit} value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={3} className="mt-1" placeholder="A short bio your AI receptionist can use to introduce you." /></div>
      {canEdit && <Button data-testid="profile-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile</Button>}
    </div>
  );
}
